/* eslint-disable @angular-eslint/directive-class-suffix */
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { debounceTime, fromEvent, mergeMap, of, Subscription, switchMap } from 'rxjs';

@Directive({ selector: '[carouselNext]', standalone: true })
export class CarouselNext {}

@Directive({ selector: '[carouselPrev]', standalone: true })
export class CarouselPrev {}

@Directive({ selector: '[carouselTitle]', standalone: true })
export class CarouselTitle {}

@Component({
  selector: 'sharry-carousel',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, NgStyle],
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent<T extends { trackByProperty: string }>
  implements AfterViewInit, AfterContentInit, OnDestroy
{
  @ViewChild('scrollingCarousel')
  scrollingCarousel: ElementRef<HTMLDivElement>;
  @ContentChild(CarouselNext, { read: ElementRef, static: false })
  nextButton: ElementRef;
  @ContentChild(CarouselPrev, { read: ElementRef, static: false })
  prevButton: ElementRef;
  trackedElements: T[] = [];
  @Input() trackByProperty: string = 'id';
  @Input() grid: CarouselGrid = { xs: 2, sm: 2, md: 3, lg: 4, xl: 4 };
  @Input() cardTemplate: TemplateRef<any>;
  @Input() empty: TemplateRef<any>;
  @Input() gutter: number = 0;
  @Input() hasShadow = false;
  @Input() inline = false;
  @Input() usePagination = false;
  @Input() total: number;
  @Output() carouselClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() next: EventEmitter<void> = new EventEmitter<void>();
  @Output() prev: EventEmitter<void> = new EventEmitter<void>();
  @Output() carouselWheel: EventEmitter<void> = new EventEmitter<void>();
  disableLeft = true;
  disableRight = false;
  visibleButtons = false;
  private inMemoryScroll: number;
  private subscription: Subscription;
  private touchStartsubscription: Subscription;
  private prevSubscription: Subscription;
  private nextSubscription: Subscription;

  constructor(
    private cd: ChangeDetectorRef,
    @Inject(WINDOW) private window: Window,
  ) {}

  @Input()
  get cards(): any[] {
    return (this.trackedElements || []).map(t => {
      const card = { ...t };
      delete card.trackByProperty;
      return card;
    });
  }

  set cards(cards: any[]) {
    this.trackedElements = (cards || []).map(c => ({
      ...c,
      trackByProperty: c[this.trackByProperty],
    }));
  }

  @HostListener('window:resize') onResize() {
    if (this.window.innerWidth >= 1280) {
      this.visibleButtons = ((this.usePagination && this.total) ?? this.trackedElements.length) > this.grid.xl;
    }
    if (this.window.innerWidth < 1280 && this.window.innerWidth >= 1024) {
      this.visibleButtons = ((this.usePagination && this.total) ?? this.trackedElements.length) > this.grid.lg;
    }
    if (this.window.innerWidth < 1024 && this.window.innerWidth >= 768) {
      this.visibleButtons = ((this.usePagination && this.total) ?? this.trackedElements.length) > this.grid.md;
    }
    if (this.window.innerWidth < 768) {
      this.visibleButtons = false;
    }
    this.cd.detectChanges();
  }

  ngAfterContentInit(): void {
    if (this.prevButton) {
      this.prevSubscription = fromEvent<MouseEvent>(this.prevButton.nativeElement, 'click').subscribe({
        next: event => {
          event.stopPropagation();
          event.stopImmediatePropagation();
          event.preventDefault();
          if (!this.disableLeft) {
            this.prev.emit();
            this.scroll('left');
          }
        },
      });
    }
    if (this.nextButton) {
      this.nextSubscription = fromEvent<MouseEvent>(this.nextButton.nativeElement, 'click').subscribe({
        next: event => {
          event.stopPropagation();
          event.stopImmediatePropagation();
          event.preventDefault();
          if (!this.disableRight) {
            this.next.emit();
            this.scroll('right');
          }
        },
      });
    }
  }

  ngAfterViewInit(): void {
    this.onResize();
    this.subscription = fromEvent<WheelEvent>(this.scrollingCarousel.nativeElement, 'wheel', { passive: true })
      .pipe(debounceTime(500))
      .subscribe({
        next: event => {
          if (event.deltaX) {
            this.carouselWheel.emit();
            this.updateDisableProperties();
          }
        },
      });

    this.touchStartsubscription = fromEvent<TouchEvent>(this.scrollingCarousel.nativeElement, 'touchstart', {
      passive: true,
    })
      .pipe(
        switchMap(touchStartEvent =>
          fromEvent<TouchEvent>(this.scrollingCarousel.nativeElement, 'touchend', {
            passive: true,
          }).pipe(
            mergeMap(touchEndEvent =>
              of(Math.abs(touchEndEvent.changedTouches[0].screenX - touchStartEvent.changedTouches[0].screenX)),
            ),
          ),
        ),
        debounceTime(500),
      )
      .subscribe({
        next: deltaX => {
          if (deltaX) {
            this.carouselWheel.emit();
          }
        },
      });
  }

  updateDisableProperties() {
    const offset = 10; // empiric value
    const totalScroll = this.scrollingCarousel.nativeElement.scrollWidth;
    const scrollAmount = this.scrollingCarousel.nativeElement.clientWidth;
    this.disableLeft = this.scrollingCarousel.nativeElement.scrollLeft === 0;
    this.disableRight =
      this.scrollingCarousel.nativeElement.scrollLeft < totalScroll - scrollAmount + offset &&
      this.scrollingCarousel.nativeElement.scrollLeft > totalScroll - scrollAmount - offset;
    this.cd.detectChanges();
  }

  scroll(direction: 'right' | 'left') {
    this.disableLeft = true;
    this.disableRight = true;
    this.cd.detectChanges();
    const scrollLeft = this.scrollingCarousel.nativeElement.scrollLeft;
    const scrollAmount: number =
      Math.floor(
        this.scrollingCarousel.nativeElement.clientWidth /
          this.scrollingCarousel.nativeElement.firstElementChild.clientWidth,
      ) * this.scrollingCarousel.nativeElement.firstElementChild.clientWidth;
    const leftover = this.calcLeftover(scrollLeft, scrollAmount);
    let adjust = 0;
    if (leftover % scrollAmount !== 0) {
      adjust = leftover;
    }
    this.scrollByAmount(direction, adjust, scrollAmount);
    this.updateScrollState(scrollLeft, adjust);
  }

  scrollByAmount(direction: 'right' | 'left', _adjust: number, scrollAmount: number) {
    const mlt = direction === 'left' ? -1 : 1;
    const adjust = this.inMemoryScroll !== _adjust ? _adjust : 0;
    const left = (this.inMemoryScroll || scrollAmount) * mlt - adjust;
    this.scrollingCarousel.nativeElement.scrollBy({
      behavior: 'smooth',
      left,
    });
  }

  updateScrollState(scrollLeft: number, adjust: number) {
    const onScroll = () => {
      if (this.scrollingCarousel.nativeElement.scrollLeft !== scrollLeft) {
        const manualScrollAmount = Math.abs(this.scrollingCarousel.nativeElement.scrollLeft - scrollLeft);
        if (manualScrollAmount !== this.inMemoryScroll && !adjust) {
          this.inMemoryScroll = manualScrollAmount;
        } else {
          this.inMemoryScroll = null;
        }
      }
      this.updateDisableProperties();
      this.scrollingCarousel.nativeElement.removeEventListener('scrollend', onScroll);
    };
    this.scrollingCarousel.nativeElement.addEventListener('scrollend', onScroll);
  }

  calcLeftover(leftover: number, total: number): number {
    if (leftover > total) {
      const difference = leftover - total;
      return this.calcLeftover(difference, total);
    }
    if (leftover !== total && leftover > total / 2) {
      leftover = leftover - total / 2;
    }
    return leftover;
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.prevSubscription?.unsubscribe();
    this.nextSubscription?.unsubscribe();
  }
}

export interface CarouselGrid {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}
